import { API } from "./api";

const mapPriority = (value) => {
  if (!value) return "MEDIUM";
  const normalized = String(value).toUpperCase();
  return ["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"].includes(normalized)
    ? normalized
    : "MEDIUM";
};

const normalizeStatusPayload = (input) => {
  if (!input) {
    throw new Error("Ticket status is required");
  }
  if (typeof input === "string") {
    return { status: input };
  }
  const { status, note, reason } = input;
  if (!status) {
    throw new Error("Ticket status is required");
  }
  const payload = { status };
  if (note) {
    payload.note = note;
  }
  if (reason) {
    payload.reason = reason;
  }
  return payload;
};

const normalizeAssigneePayload = (input) => {
  if (input === undefined || input === null) {
    return { assignedTo: null };
  }
  if (Array.isArray(input)) {
    // Send the array as-is (backend expects { assignedTo: [...] })
    return { assignedTo: input };
  }
  if (typeof input === "string") {
    return { assignedTo: input || null };
  }
  if (typeof input === "object" && Object.prototype.hasOwnProperty.call(input, "assignedTo")) {
    return { assignedTo: input.assignedTo || null };
  }
  return { assignedTo: null };
};

export const createSupportTicket = async ({ title, message, description, category, priority, requesterEmail, requesterName, requesterCompany, requesterRole, source, attachments }) => {
  const form = new FormData();
  if (title) form.append("title", title);
  form.append("description", description || message || "");
  if (category) form.append("category", category);
  form.append("priority", mapPriority(priority));
  if (requesterEmail) form.append("requesterEmail", requesterEmail);
  if (requesterName) form.append("requesterName", requesterName);
  if (requesterRole) form.append("requesterRole", requesterRole);
  if (requesterCompany) form.append("requesterCompany", requesterCompany);
  form.append("source", source || "web");

  if (attachments && Array.isArray(attachments)) {
    attachments.forEach((file) => {
      if (file) form.append("attachments", file);
    });
  }

  const response = await API.post("/support/tickets", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data?.data;
};

export const listSupportTickets = async (params) => {
  const response = await API.get("/support/tickets", { params });
  return response.data?.data;
};


export const getSupportTicket = async (ticketId) => {
  const response = await API.get(`/support/tickets/${ticketId}`);
  return response.data?.data;
};

export const updateSupportTicketStatus = async (ticketId, payload) => {
  const response = await API.patch(`/support/tickets/${ticketId}/status`, normalizeStatusPayload(payload));
  return response.data?.data;
};

export const assignSupportTicket = async (ticketId, payload) => {
  const response = await API.patch(`/support/tickets/${ticketId}/assignee`, normalizeAssigneePayload(payload));
  return response.data?.data;
};

export const deleteSupportTicket = async (ticketId, payload = {}) => {
  const response = await API.delete(`/support/tickets/${ticketId}`, {
    data: payload,
    params: payload,
  });
  return response.data?.data;
};

export const updateSupportTicket = async (ticketId, formData) => {
  const response = await API.patch(`/support/tickets/${ticketId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data?.data;
};

export const getSupportTicketMetrics = async (params) => {
  const response = await API.get("/support/tickets/metrics/dashboard", { params });
  return response.data?.data;
};

export const submitGeneralFeedback = async (payload) => {
  const response = await API.post("/support/feedback", payload);
  return response.data?.data;
};

export const listGeneralFeedback = async (params) => {
  const response = await API.get("/support/feedback", { params });
  return response.data?.data;
};

export const getFeedbackAnalytics = async (params) => {
  const response = await API.get("/support/feedback/analytics", { params });
  return response.data?.data;
};

export const deleteFeedback = async (feedbackId) => {
  const response = await API.delete(`/support/feedback/${feedbackId}`);
  return response.data?.data;
};



export const fetchHelpCenterArticles = async (params) => {
  const response = await API.get("/support/help-center/articles", { params });
  return response.data?.data;
};

export const fetchAdminHelpCenterArticles = async (params) => {
  const response = await API.get("/support/help-center/admin/articles", { params });
  return response.data?.data;
};

export const fetchHelpCenterArticle = async (slug) => {
  const response = await API.get(`/support/help-center/articles/${slug}`);
  return response.data?.data;
};

export const createHelpArticle = async (payload) => {
  const response = await API.post("/support/help-center/articles", payload);
  return response.data?.data;
};

export const updateHelpArticle = async (articleId, payload) => {
  const response = await API.patch(`/support/help-center/articles/${articleId}`, payload);
  return response.data?.data;
};

export const publishHelpArticle = async (articleId) => {
  const response = await API.post(`/support/help-center/articles/${articleId}/publish`);
  return response.data?.data;
};

export const unpublishHelpArticle = async (articleId) => {
  const response = await API.post(`/support/help-center/articles/${articleId}/unpublish`);
  return response.data?.data;
};

export const deleteHelpArticle = async (articleId) => {
  const response = await API.delete(`/support/help-center/articles/${articleId}`);
  return response.data?.data;
};

export const toggleFeedbackHighlight = async (feedbackId) => {
  const response = await API.patch(`/support/feedback/${feedbackId}/toggle-highlight`);
  return response.data?.data;
};

export const getHighlightedFeedback = async () => {
  const response = await API.get("/support/feedback/highlighted");
  return response.data?.data;
};
